import { filter, reject } from "lodash/fp";

import GmailLogo from "@app/common/assets/GmailLogo@2x.png";
import GoogleCalendarLogo from "@app/common/assets/GoogleCalendarLogo2x.png";
import GoogleLogo from "@app/common/assets/GoogleLogo@2x.png";
import HubSpotLogo from "@app/common/assets/HubSpotLogo.png";
import MicrosoftLogo from "@app/common/assets/MicrosoftLogoIcon.png";
import MicrosoftOutlookLogo from "@app/common/assets/MicrosoftOutlookLogo@2x.png";
import SalesforceLogo from "@app/common/assets/SalesforceLogoSquare.png";
import SlackLogo from "@app/common/assets/SlackLogo@3x.png";
import MicrosoftTeamsLogo from "@app/common/assets/TeamsLogoNoBackground.png";
import WebexLogo from "@app/common/assets/WebexLogo@2x.png";
import WebhooksIcon from "@app/common/assets/WebhooksIcon@2x.png";
import ZapierLogo from "@app/common/assets/ZapierLogo@2x.png";
import ZoomLogo from "@app/common/assets/ZoomLogoRounded@2x.png";
import ZoomLogoLarge from "@app/common/assets/ZoomLogoRounded@2x.png";
import LINKS from "@app/common/links";
import ROUTES from "@app/common/routes";
import type {
  AppProvider,
  AppProviderId,
  CalendarProviderId,
  CalendarProvider,
  DataIngestProvider,
  DataIngestProviderId,
  EmailProvider,
  EmailProviderId,
  IntegrationProvider,
  IntegrationProviderId,
  MessagingBotProvider,
  MessagingBotProviderId,
  ProductivityProvider,
  ProductivityProviderId,
  Provider,
  ProviderId,
  SSOProvider,
  SSOProviderId,
  WorkspaceIntegrationProvider,
  CrmProviderId,
  CrmProvider,
} from "@app/common/types/providerTypes";
import { canAccessSalesforce } from "@app/common/utils";
import Config from "@app/config";

export const SSO_PROVIDERS_BY_ID: Readonly<{
  [key in SSOProviderId]: SSOProvider;
}> = {
  google: {
    id: "google",
    connectAction: "google_sso_connect",
    connectUrl: Config.auth.google.AUTH_URL,
    displayName: "Google",
    displayNameShort: "Google",
    icon: GoogleLogo,
    integrationSettingsKinds: ["google_auth"],
  },
  microsoft_graph: {
    id: "microsoft_graph",
    connectAction: "microsoft_sso_connect",
    connectUrl: Config.auth.microsoft_graph.AUTH_URL,
    displayName: "Microsoft",
    displayNameShort: "Microsoft",
    icon: MicrosoftLogo,
    integrationSettingsKinds: ["microsoft_graph_auth"],
  },
  slack_sso: {
    id: "slack_sso",
    connectAction: "slack_sso_connect",
    connectUrl: Config.auth.slack_sso.AUTH_URL,
    displayName: "Slack",
    displayNameShort: "Slack",
    icon: SlackLogo,
    integrationSettingsKinds: ["slack_auth"],
  },
  webex: {
    id: "webex",
    connectAction: "webex_sso_connect",
    connectUrl: Config.auth.webex.AUTH_URL,
    displayName: "Webex",
    displayNameShort: "Webex",
    icon: WebexLogo,
    integrationSettingsKinds: ["webex_auth"],
  },
  zoom: {
    id: "zoom",
    connectAction: "zoom_sso_connect",
    connectUrl: Config.auth.zoom.AUTH_URL,
    displayName: "Zoom",
    displayNameShort: "Zoom",
    icon: ZoomLogo,
    integrationSettingsKinds: ["zoom_auth"],
  },
};

export const CALENDAR_PROVIDERS_BY_ID: Readonly<{
  [key in CalendarProviderId]: CalendarProvider;
}> = {
  google_calendar: {
    id: "google_calendar",
    kind: "calendar",
    accessSettings: ["integrationGoogleCalendarEnabled"],
    category: "calendar",
    connectAction: "google_calendar_connect",
    connectUrl: Config.calendar.GOOGLE_URL,
    disconnectUrl: null,
    displayName: "Google Calendar",
    displayNameShort: "Google Calendar",
    enableUrl: Config.auth.google_calendar.ENABLE_URL,
    getUrl: null,
    icon: GoogleCalendarLogo,
    integrationSettingsKinds: ["google_calendar"],
    integrationUrl: ROUTES.INTEGRATIONS_GOOGLE_CALENDAR,
    isHighValue: true,
    isPremium: false,
    supportedScopes: ["user"],
    supportsRssLinks: true,
  },
  microsoft_graph_calendar: {
    id: "microsoft_graph_calendar",
    kind: "calendar",
    accessSettings: ["integrationMicrosoftGraphCalendarEnabled"],
    category: "calendar",
    connectAction: "microsoft_calendar_connect",
    connectUrl: Config.calendar.MICROSOFT_URL,
    disconnectUrl: null,
    displayName: "Microsoft Outlook",
    displayNameShort: "Microsoft Outlook",
    enableUrl: Config.auth.microsoft_graph_calendar.ENABLE_URL,
    getUrl: null,
    icon: MicrosoftOutlookLogo,
    integrationSettingsKinds: ["microsoft_graph_calendar"],
    integrationUrl: ROUTES.INTEGRATIONS_OUTLOOK_CALENDAR,
    isHighValue: true,
    isPremium: false,
    supportedScopes: ["user"],
    supportsRssLinks: true,
  },
  zoom_calendar: {
    id: "zoom_calendar",
    kind: "calendar",
    category: "calendar",
    accessSettings: ["integrationZoomCalendarEnabled"],
    connectAction: "zoom_calendar_connect",
    connectUrl: Config.calendar.ZOOM_URL,
    disconnectUrl: null,
    displayName: "Zoom Calendar",
    displayNameShort: "Zoom Calendar",
    getUrl: null,
    icon: ZoomLogoLarge,
    integrationSettingsKinds: ["zoom_calendar"],
    integrationUrl: ROUTES.INTEGRATIONS_ZOOM_CALENDAR,
    isHighValue: false,
    isPremium: false,
    supportedScopes: ["user"],
    supportsRssLinks: false,
  },
};

export const DATA_INGEST_PROVIDERS_BY_ID: Readonly<{
  [key in DataIngestProviderId]: DataIngestProvider;
}> = {
  microsoft_graph_teams_chat_ingest: {
    id: "microsoft_graph_teams_chat_ingest",
    kind: "data_ingest",
    accessSettings: ["integrationMicrosoftGraphTeamsChatIngestEnabled"],
    category: "productivity",
    connectAction: "teams_ingest_connect",
    connectUrl: Config.integrations.microsoft_graph_teams_chat_ingest.AUTH_URL,
    disconnectUrl:
      Config.integrations.microsoft_graph_teams_chat_ingest.CONNECTION_URL,
    displayName: "Teams Sync to Read",
    displayNameShort: "Teams",
    enableUrl:
      Config.integrations.microsoft_graph_teams_chat_ingest.CONNECTION_URL,
    getUrl:
      Config.integrations.microsoft_graph_teams_chat_ingest.CONNECTION_URL,
    icon: MicrosoftTeamsLogo,
    integrationSettingsKinds: ["microsoft_graph_teams_chat_ingest"],
    integrationUrl: ROUTES.INTEGRATIONS_TEAMS_INGEST,
    isPremium: false,
    supportedScopes: ["user"],
  },
  slack_ingest: {
    id: "slack_ingest",
    kind: "data_ingest",
    accessSettings: ["integrationSlackIngestEnabled"],
    category: "productivity",
    connectAction: "slack_ingest_connect",
    connectUrl: Config.integrations.slack_ingest.AUTH_URL,
    disconnectUrl: Config.integrations.slack_ingest.CONNECTION_URL,
    displayName: "Slack Sync to Read",
    displayNameShort: "Slack",
    documentationUrl:
      "https://support.read.ai/hc/en-us/articles/26435961740691-Integrations-Slack",
    getUrl: Config.integrations.slack_ingest.CONNECTION_URL,
    icon: SlackLogo,
    integrationSettingsKinds: ["slack_ingest"],
    integrationUrl: ROUTES.INTEGRATIONS_SLACK_INGEST,
    isPremium: false,
    supportedScopes: ["user"],
  },
};

export const EMAIL_PROVIDERS_BY_ID: Readonly<{
  [key in EmailProviderId]: EmailProvider;
}> = {
  google_email: {
    id: "google_email",
    kind: "email",
    accessSettings: ["integrationGoogleEmailEnabled"],
    category: "productivity",
    connectAction: "gmail_connect",
    connectUrl: Config.integrations.google_email.AUTH_URL,
    disconnectUrl: Config.integrations.google_email.CONNECTION_URL,
    displayName: "Gmail Sync to Read",
    displayNameShort: "Gmail",
    enableUrl: Config.integrations.google_email.CONNECTION_URL,
    getUrl: Config.integrations.google_email.CONNECTION_URL,
    icon: GmailLogo,
    integrationSettingsKinds: ["google_email"],
    integrationUrl: ROUTES.INTEGRATIONS_GOOGLE_EMAIL,
    isPremium: false,
    supportedScopes: ["user"],
  },
  microsoft_graph_email: {
    id: "microsoft_graph_email",
    kind: "email",
    accessSettings: ["integrationMicrosoftGraphEmailEnabled"],
    category: "productivity",
    connectAction: "microsoft_email_connect",
    connectUrl: Config.integrations.microsoft_graph_email.AUTH_URL,
    disconnectUrl: Config.integrations.microsoft_graph_email.CONNECTION_URL,
    displayName: "Outlook Sync to Read",
    displayNameShort: "Outlook",
    enableUrl: Config.integrations.microsoft_graph_email.CONNECTION_URL,
    getUrl: Config.integrations.microsoft_graph_email.CONNECTION_URL,
    icon: MicrosoftOutlookLogo,
    integrationSettingsKinds: ["microsoft_graph_email"],
    integrationUrl: ROUTES.INTEGRATIONS_OUTLOOK_EMAIL,
    isPremium: false,
    supportedScopes: ["user"],
  },
};

export const MESSAGING_BOT_PROVIDERS_BY_ID: Readonly<{
  [key in MessagingBotProviderId]: MessagingBotProvider;
}> = {
  microsoft_graph_teams_push: {
    id: "microsoft_graph_teams_push",
    kind: "messaging_bot",
    accessSettings: ["integrationMicrosoftGraphTeamsPushEnabled"],
    category: "messaging",
    connectAction: "teams_bot_connect",
    connectUrl: Config.integrations.microsoft_graph_teams_push.AUTH_URL,
    disconnectUrl:
      Config.integrations.microsoft_graph_teams_push.CONNECTION_URL,
    displayName: "Push to Teams",
    displayNameShort: "Teams",
    enableUrl: Config.integrations.microsoft_graph_teams_push.CONNECTION_URL,
    getUrl: Config.integrations.microsoft_graph_teams_push.CONNECTION_URL,
    icon: MicrosoftTeamsLogo,
    integrationSettingsKinds: ["microsoft_graph_teams_push"],
    integrationUrl: ROUTES.INTEGRATIONS_USER_TEAMS,
    isPremium: false,
    supportedScopes: ["user"],
    workspaceIntegrationUrl: "",
  },
  slack_bot: {
    id: "slack_bot",
    kind: "messaging_bot",
    accessSettings: ["integrationSlackBotEnabled"],
    category: "messaging",
    connectAction: "slack_bot_connect",
    connectUrl: Config.integrations.slack_bot.AUTH_URL,
    disconnectUrl: "",
    displayName: "Push to Slack",
    displayNameShort: "Slack",
    documentationUrl: LINKS.SUPPORT.CONNECT_USER_SLACK,
    getUrl: "",
    icon: SlackLogo,
    integrationSettingsKinds: ["slack_bot"],
    integrationUrl: ROUTES.INTEGRATIONS_USER_SLACK,
    isPremium: false,
    supportedScopes: ["user", "workspace"],
    workspaceDocumentationUrl: "",
    workspaceIntegrationUrl: ROUTES.INTEGRATIONS_WORKSPACE_SLACK,
  },
};

export const CRM_PROVIDERS_BY_ID: Readonly<{
  [key in CrmProviderId]: CrmProvider;
}> = {
  hubspot: {
    id: "hubspot",
    kind: "crm",
    category: "crm",
    accessSettings: [
      "integrationHubspotPullEnabled",
      "integrationHubspotPushEnabled",
    ],
    connectAction: "hubspot_connect",
    connectUrl: Config.integrations.hubspot.AUTH_URL,
    disconnectUrl: Config.integrations.hubspot.CONNECTION_URL,
    displayName: "HubSpot",
    displayNameShort: "HubSpot",
    documentationUrl: LINKS.SUPPORT.CONNECT_HUBSPOT,
    getUrl: Config.integrations.hubspot.CONNECTION_URL,
    icon: HubSpotLogo,
    integrationSettingsKinds: ["hubspot_pull", "hubspot_push"],
    integrationUrl: ROUTES.INTEGRATIONS_HUBSPOT,
    isPremium: true,
    supportedScopes: ["user"],
  },
  salesforce: {
    id: "salesforce",
    kind: "crm",
    category: "crm",
    accessSettings: [
      "integrationSalesforcePullEnabled",
      "integrationSalesforcePushEnabled",
    ],
    connectAction: "salesforce_connect",
    connectUrl: Config.integrations.salesforce.AUTH_URL,
    disconnectUrl: Config.integrations.salesforce.CONNECTION_URL,
    displayName: "Salesforce",
    displayNameShort: "Salesforce",
    getUrl: Config.integrations.salesforce.CONNECTION_URL,
    icon: SalesforceLogo,
    integrationSettingsKinds: ["salesforce_pull", "salesforce_push"],
    integrationUrl: ROUTES.INTEGRATIONS_SALESFORCE,
    isPremium: true,
    supportedScopes: ["user"],
    disabled: () => !canAccessSalesforce(),
  },
};

export const APP_PROVIDERS_BY_ID: Readonly<{
  [key in AppProviderId]: AppProvider;
}> = {
  webhooks: {
    id: "webhooks",
    kind: "app",
    accessSettings: ["integrationWebhooksEnabled"],
    category: "workflow",
    connectAction: null,
    connectUrl: null,
    disconnectUrl: null,
    displayName: "Webhooks",
    displayNameShort: "Webhooks",
    documentationUrl: LINKS.SUPPORT.CREATE_WEBHOOK_INTEGRATION,
    getUrl: null,
    icon: WebhooksIcon,
    integrationSettingsKinds: ["webhooks"],
    integrationUrl: ROUTES.INTEGRATIONS_WEBHOOKS,
    isPremium: true,
    supportedScopes: ["user"],
  },
  zapier: {
    id: "zapier",
    kind: "app",
    accessSettings: ["integrationZapierEnabled"],
    category: "workflow",
    connectAction: null,
    connectUrl: null,
    disconnectUrl: null,
    displayName: "Zapier",
    displayNameShort: "Zapier",
    documentationUrl: LINKS.SUPPORT.CONFIGURE_ZAPIER,
    getUrl: null,
    icon: ZapierLogo,
    integrationSettingsKinds: ["zapier"],
    integrationUrl: ROUTES.INTEGRATIONS_ZAPIER,
    isPremium: true,
    supportedScopes: ["user"],
  },
};

export const CALENDAR_PROVIDER_FOR_SSO_PROVIDER: Readonly<{
  [key in SSOProviderId]: CalendarProviderId | null;
}> = {
  google: "google_calendar",
  microsoft_graph: "microsoft_graph_calendar",
  slack_sso: null,
  webex: null,
  zoom: "zoom_calendar",
};

export const PRODUCTIVITY_PROVIDERS_BY_ID: {
  [key in ProductivityProviderId]: ProductivityProvider;
} = {
  ...DATA_INGEST_PROVIDERS_BY_ID,
  ...EMAIL_PROVIDERS_BY_ID,
};

export const INTEGRATION_PROVIDERS_BY_ID: {
  [key in IntegrationProviderId]: IntegrationProvider;
} = {
  ...CALENDAR_PROVIDERS_BY_ID,
  ...DATA_INGEST_PROVIDERS_BY_ID,
  ...EMAIL_PROVIDERS_BY_ID,
  ...MESSAGING_BOT_PROVIDERS_BY_ID,
  ...APP_PROVIDERS_BY_ID,
  ...CRM_PROVIDERS_BY_ID,
};

export const PROVIDERS_BY_ID: {
  [key in ProviderId]: Provider;
} = {
  ...SSO_PROVIDERS_BY_ID,
  ...INTEGRATION_PROVIDERS_BY_ID,
};

export const SSO_PROVIDERS: Readonly<SSOProvider[]> = [
  SSO_PROVIDERS_BY_ID.google,
  SSO_PROVIDERS_BY_ID.microsoft_graph,
  SSO_PROVIDERS_BY_ID.zoom,
  SSO_PROVIDERS_BY_ID.webex,
  SSO_PROVIDERS_BY_ID.slack_sso,
];

export const SIGNUP_SSO_PROVIDERS: Readonly<SSOProvider[]> = reject(
  { id: "webex" },
  SSO_PROVIDERS
);

export const CALENDAR_PROVIDERS: Readonly<CalendarProvider[]> = [
  CALENDAR_PROVIDERS_BY_ID.google_calendar,
  CALENDAR_PROVIDERS_BY_ID.microsoft_graph_calendar,
  CALENDAR_PROVIDERS_BY_ID.zoom_calendar,
];

export const RSS_CALENDAR_PROVIDERS: Readonly<CalendarProvider[]> = filter(
  { supportsRssLinks: true },
  CALENDAR_PROVIDERS
);

export const DATA_INGEST_PROVIDERS: Readonly<DataIngestProvider[]> = [
  DATA_INGEST_PROVIDERS_BY_ID.slack_ingest,
  DATA_INGEST_PROVIDERS_BY_ID.microsoft_graph_teams_chat_ingest,
];

export const EMAIL_PROVIDERS: Readonly<EmailProvider[]> = [
  EMAIL_PROVIDERS_BY_ID.google_email,
  EMAIL_PROVIDERS_BY_ID.microsoft_graph_email,
];

export const MESSAGING_BOT_PROVIDERS: Readonly<MessagingBotProvider[]> = [
  MESSAGING_BOT_PROVIDERS_BY_ID.slack_bot,
  MESSAGING_BOT_PROVIDERS_BY_ID.microsoft_graph_teams_push,
];

export const APP_PROVIDERS: Readonly<AppProvider[]> = [
  APP_PROVIDERS_BY_ID.webhooks,
  APP_PROVIDERS_BY_ID.zapier,
];

export const PRODUCTIVITY_PROVIDERS: Readonly<ProductivityProvider[]> = [
  ...EMAIL_PROVIDERS,
  ...DATA_INGEST_PROVIDERS,
];

export const CRM_PROVIDERS: Readonly<CrmProvider[]> = [
  CRM_PROVIDERS_BY_ID.hubspot,
  CRM_PROVIDERS_BY_ID.salesforce,
];

export const INTEGRATION_PROVIDERS: Readonly<IntegrationProvider[]> = [
  ...CALENDAR_PROVIDERS,
  ...EMAIL_PROVIDERS,
  ...DATA_INGEST_PROVIDERS,
  ...MESSAGING_BOT_PROVIDERS,
  ...APP_PROVIDERS,
  ...CRM_PROVIDERS,
];

export const WORKSPACE_INTEGRATION_PROVIDERS: Readonly<
  WorkspaceIntegrationProvider[]
> = filter(
  ({ supportedScopes }) => supportedScopes.includes("workspace"),
  INTEGRATION_PROVIDERS
) as WorkspaceIntegrationProvider[];
